import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const AnimatedLogoBackground = () => {
  const canvasRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('drawing');
  const [logoLoaded, setLogoLoaded] = useState(false);
  const logoImgRef = useRef(null);

  useEffect(() => {
    // تحميل صورة اللوقو
    const img = new Image();
    img.src = '/yamai-logo.png'; // ضع مسار اللوقو هنا
    img.onload = () => {
      logoImgRef.current = img;
      setLogoLoaded(true);
    };
    img.onerror = () => {
      console.log('Logo image not found');
      setLogoLoaded(false);
    };
  }, []);

  useEffect(() => {
    if (!logoLoaded) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const logoSize = Math.min(canvas.width, canvas.height) * 0.25;

    // خطوط الرسم التي تشكل حول اللوقو
    const drawingLines = [];
    const numLines = 60;
    
    for (let i = 0; i < numLines; i++) {
      const angle = (i / numLines) * Math.PI * 2;
      const radius = logoSize * 0.6;
      const variance = Math.random() * 40 - 20;
      
      drawingLines.push({
        startX: centerX + Math.cos(angle) * (radius + variance + 100),
        startY: centerY + Math.sin(angle) * (radius + variance + 100),
        endX: centerX + Math.cos(angle) * (radius + variance),
        endY: centerY + Math.sin(angle) * (radius + variance),
        progress: 0,
        delay: i * 15,
        hue: 180 + (i / numLines) * 100,
        thickness: 1 + Math.random() * 2
      });
    }

    let frame = 0;
    let logoRevealProgress = 0;
    let phaseTimer = 0;
    let animationId;

    const animate = () => {
      ctx.fillStyle = 'rgba(10, 15, 30, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      frame++;
      phaseTimer++;

      if (animationPhase === 'drawing') {
        // رسم الخطوط المتحركة
        drawingLines.forEach(line => {
          if (frame > line.delay) {
            line.progress = Math.min(line.progress + 0.02, 1);
            
            const currentX = line.startX + (line.endX - line.startX) * line.progress;
            const currentY = line.startY + (line.endY - line.startY) * line.progress;
            
            ctx.strokeStyle = `hsla(${line.hue}, 80%, 60%, ${line.progress * 0.3})`;
            ctx.lineWidth = line.thickness;
            ctx.lineCap = 'round';
            ctx.shadowBlur = 10;
            ctx.shadowColor = `hsla(${line.hue}, 80%, 60%, 0.2)`;
            
            ctx.beginPath();
            ctx.moveTo(line.startX, line.startY);
            ctx.lineTo(currentX, currentY);
            ctx.stroke();
            
            ctx.shadowBlur = 0;
          }
        });

        const allDrawn = drawingLines.every(line => line.progress >= 0.95);
        if (allDrawn && phaseTimer > 60) {
          setAnimationPhase('revealing');
          phaseTimer = 0;
        }
      }

      if (animationPhase === 'revealing') {
        // تلاشي الخطوط
        drawingLines.forEach(line => {
          const fadeProgress = Math.max(0, 1 - (phaseTimer / 60));
          
          ctx.strokeStyle = `hsla(${line.hue}, 80%, 60%, ${fadeProgress * 0.25})`;
          ctx.lineWidth = line.thickness;
          ctx.lineCap = 'round';
          ctx.shadowBlur = 8;
          ctx.shadowColor = `hsla(${line.hue}, 80%, 60%, ${fadeProgress * 0.15})`;
          
          ctx.beginPath();
          ctx.moveTo(line.startX, line.startY);
          ctx.lineTo(line.endX, line.endY);
          ctx.stroke();
          
          ctx.shadowBlur = 0;
        });

        // إظهار اللوقو تدريجياً
        logoRevealProgress = Math.min(logoRevealProgress + 0.015, 1);
        
        if (logoImgRef.current) {
          ctx.save();
          ctx.globalAlpha = logoRevealProgress * 0.4;
          ctx.shadowBlur = 20;
          ctx.shadowColor = `rgba(0, 188, 212, ${logoRevealProgress * 0.2})`;
          
          ctx.drawImage(
            logoImgRef.current,
            centerX - logoSize / 2,
            centerY - logoSize / 2,
            logoSize,
            logoSize
          );
          
          ctx.shadowBlur = 0;
          ctx.restore();
        }

        if (logoRevealProgress >= 1 && phaseTimer > 90) {
          setAnimationPhase('floating');
        }
      }

      if (animationPhase === 'floating') {
        // اللوقو مع تأثير العوم
        if (logoImgRef.current) {
          ctx.save();
          
          const floatY = Math.sin(frame * 0.02) * 8;
          const scale = 1 + Math.sin(frame * 0.015) * 0.03;
          
          ctx.globalAlpha = 0.3;
          ctx.shadowBlur = 15;
          ctx.shadowColor = 'rgba(0, 188, 212, 0.15)';
          
          ctx.translate(centerX, centerY + floatY);
          ctx.scale(scale, scale);
          
          ctx.drawImage(
            logoImgRef.current,
            -logoSize / 2,
            -logoSize / 2,
            logoSize,
            logoSize
          );
          
          ctx.shadowBlur = 0;
          ctx.restore();
        }

        // جسيمات خفيفة
        if (frame % 5 === 0) {
          const angle = Math.random() * Math.PI * 2;
          const distance = logoSize * 0.5 + Math.random() * 50;
          const x = centerX + Math.cos(angle) * distance;
          const y = centerY + Math.sin(angle) * distance;
          
          ctx.fillStyle = `hsla(${180 + Math.random() * 100}, 80%, 60%, 0.2)`;
          ctx.shadowBlur = 5;
          ctx.shadowColor = `hsla(${180 + Math.random() * 100}, 80%, 60%, 0.1)`;
          
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.shadowBlur = 0;
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      cancelAnimationFrame(animationId);
    };
  }, [animationPhase, logoLoaded]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none"
        style={{
          opacity: 0.25,
          zIndex: 1
        }}
      />

      {animationPhase === 'floating' && (
        <motion.div 
          className="fixed inset-0 pointer-events-none overflow-hidden" 
          style={{ zIndex: 2 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={`ring-${i}`}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{
                width: `${200 + i * 80}px`,
                height: `${200 + i * 80}px`,
              }}
              animate={{
                rotate: [0, 360],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                rotate: { duration: 15 + i * 5, repeat: Infinity, ease: 'linear' },
                opacity: { duration: 3, repeat: Infinity }
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke={`hsl(${180 + i * 30}, 80%, 60%)`}
                  strokeWidth="0.5"
                  strokeDasharray="5 5"
                  opacity="0.4"
                />
              </svg>
            </motion.div>
          ))}

          {[...Array(16)].map((_, i) => (
            <motion.div
              key={`particle-${i}`}
              className="absolute w-1 h-1 rounded-full left-1/2 top-1/2"
              style={{
                background: `hsl(${180 + i * 8}, 80%, 60%)`
              }}
              animate={{
                x: [0, Math.cos(i * 22.5 * Math.PI / 180) * 150, 0],
                y: [0, Math.sin(i * 22.5 * Math.PI / 180) * 150, 0],
                scale: [0, 1.5, 0],
                opacity: [0, 0.8, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.15,
                ease: 'easeInOut'
              }}
            />
          ))}
        </motion.div>
      )}
    </>
  );
};

export default AnimatedLogoBackground;
